import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@wms/shared';
import { apiFetch, ApiError } from '../../lib/api';
import { useCan } from '../../lib/session';
import type { ProductDetail } from './types';
import type { UomItem } from './types';
import { ConversionPreview, toUnitDefinitions } from './quantity-display';
import { UomCreateModal } from './uom-create-modal';

/** 与 product-create-page.tsx 同一约定：下拉里"＋ 新建单位…"的占位 value */
const CREATE_UOM_VALUE = '__create_uom__';

/**
 * 单位与包装标签页（S8）：基本单位（创建后不可改，见 0008 迁移的列级授权）+
 * 包装单位表 + 新增单位表单 + 换算预览（"1 托 = 40 箱 = 960 支"演示，退出标准）。
 *
 * I10：换算因子创建后即不可修改——不是"填了就灰掉"的前端限制，是后端
 * 从数据库权限层面就没有开放这条写路径（product/products.service.ts 顶部
 * 注释）。这里的 UI 因此从不提供"编辑因子"的入口，只提供"新增单位"和
 * "归档单位"两个操作，忠实反映后端唯一支持的整改路径。
 */
export function UnitEditor({
  product,
  canEdit,
}: {
  readonly product: ProductDetail;
  readonly canEdit: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [uomId, setUomId] = useState('');
  const [factor, setFactor] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showUomModal, setShowUomModal] = useState(false);
  const canManageSettings = useCan(PERMISSIONS.SETTINGS_MANAGE);

  const { data: uomsData } = useQuery({
    queryKey: ['uoms'],
    queryFn: () => apiFetch<{ items: UomItem[] }>('/uoms'),
  });

  const usedUomIds = new Set(product.units.filter((u) => !u.archivedAt).map((u) => u.uomId));
  const availableUoms = (uomsData?.items ?? []).filter(
    (u) => !u.archivedAt && !usedUomIds.has(u.id),
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['products', product.id] });

  const addUnit = useMutation({
    mutationFn: () =>
      apiFetch(`/products/${product.id}/units`, {
        method: 'POST',
        body: { uomId, factorToBase: factor.trim() },
      }),
    onSuccess: async () => {
      setAdding(false);
      setUomId('');
      setFactor('');
      setError(null);
      await refresh();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err)),
  });

  const archiveUnit = useMutation({
    mutationFn: (unitId: string) =>
      apiFetch(`/products/${product.id}/units/${unitId}/archive`, { method: 'POST' }),
    onSuccess: () => refresh(),
  });

  const units = toUnitDefinitions(product.units);

  return (
    <div>
      <div className="table-scroll"><table className="table">
        <thead>
          <tr>
            <th>{t('units.colUnit')}</th>
            <th>{t('units.colFactor')}</th>
            <th>{t('units.colFlags')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {product.units.map((u) => (
            <tr key={u.id} style={u.archivedAt ? { opacity: 0.5 } : undefined}>
              <td>{u.uomCode}</td>
              <td>{u.factorToBase}</td>
              <td>
                {u.isBase && <span className="badge badge--muted">{t('units.badgeBase')}</span>}
                {u.isDefaultInbound && (
                  <span className="badge badge--muted">{t('units.badgeDefaultInbound')}</span>
                )}
                {u.archivedAt && <span className="badge badge--muted">{t('status.archived')}</span>}
              </td>
              <td>
                {canEdit && !u.isBase && !u.archivedAt && (
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => archiveUnit.mutate(u.id)}
                  >
                    {t('units.archive')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>

      <p className="field-hint">{t('units.factorLockedHint')}</p>

      {canEdit && !adding && (
        <button className="btn btn--ghost btn--sm" onClick={() => setAdding(true)}>
          {t('units.addUnit')}
        </button>
      )}

      {canEdit && adding && (
        <form
          className="row"
          style={{ gap: 'var(--space-2)', alignItems: 'flex-end', marginTop: 'var(--space-2)' }}
          onSubmit={(e) => {
            e.preventDefault();
            addUnit.mutate();
          }}
        >
          <label className="field" style={{ maxWidth: '200px' }}>
            <span>{t('units.unit')}</span>
            <select
              value={uomId}
              onChange={(e) => {
                if (e.target.value === CREATE_UOM_VALUE) {
                  setShowUomModal(true);
                  return;
                }
                setUomId(e.target.value);
              }}
              required
            >
              <option value="" disabled>
                {t('units.selectUnit')}
              </option>
              {availableUoms.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.code}
                </option>
              ))}
              {canManageSettings && (
                <option value={CREATE_UOM_VALUE}>{t('units.createOption')}</option>
              )}
            </select>
          </label>
          <label className="field" style={{ maxWidth: '160px' }}>
            <span>{t('units.factor')}</span>
            <input
              value={factor}
              onChange={(e) => setFactor(e.target.value)}
              placeholder="24"
              inputMode="numeric"
              pattern="[1-9][0-9]*"
              required
            />
          </label>
          <button type="submit" className="btn btn--primary btn--sm" disabled={addUnit.isPending}>
            {t('app.save')}
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAdding(false)}>
            {t('app.cancel')}
          </button>
        </form>
      )}
      {error && <p className="field-error">{error}</p>}

      {/* 弹窗自身是 form，不能嵌在上面的新增单位 form 里 */}
      {showUomModal && (
        <UomCreateModal
          onClose={() => setShowUomModal(false)}
          onCreated={(id) => {
            setShowUomModal(false);
            setUomId(id);
          }}
        />
      )}

      {units.length > 0 && <ConversionPreview units={units} />}
    </div>
  );
}
